import { useEffect, useState } from 'react'
import { AlertTriangle, Check } from 'lucide-react'
import { api } from '@/runtime'
import type { Builds } from '@/db/api'
import { useNow } from '@/hooks/useNow'
import { pipelineOf, shipping, type Stage } from '@/lib/pipeline'
import { cx } from '@/components/ui'

// ---- the ship pipeline (DevOS → Releases, the Prime app's Overview) ---------------
//
// useBuilds polls GET /api/builds: every four seconds while a release is on its
// way, every twenty otherwise. A poll that fails mid-ship is prod restarting (the
// page's own server is the one going away), shown as such and never as an error.

export const useBuilds = () => {
  const [data, setData] = useState<Builds | null>(null)
  const [error, setError] = useState('')
  const [unreachable, setUnreachable] = useState(false)
  const [tick, setTick] = useState(0)
  const inFlight = shipping(data)
  useEffect(() => {
    let stale = false
    const load = () =>
      api
        .builds()
        .then((b) => {
          if (stale) return
          setData(b)
          setError('')
          setUnreachable(false)
        })
        .catch((e: Error) => {
          if (stale) return
          if (inFlight) setUnreachable(true)
          else setError(navigator.onLine ? e.message : 'You are offline. Releases need a connection.')
        })
    void load()
    const t = window.setInterval(
      () => document.visibilityState === 'visible' && void load(),
      inFlight ? 4_000 : 20_000,
    )
    return () => {
      stale = true
      window.clearInterval(t)
    }
  }, [tick, inFlight])
  return { data, error, unreachable, reload: () => setTick((n) => n + 1) }
}

const elapsed = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

const Dot = ({ state }: { state: Stage['state'] }) => (
  <span
    className={cx(
      'relative z-[1] h-5 w-5 rounded-full shrink-0 flex items-center justify-center border',
      state === 'done' && 'bg-emerald-500/20 border-emerald-500/60 text-emerald-400',
      state === 'active' && 'border-accent bg-accent-soft',
      state === 'waiting' && 'border-line bg-panel',
      state === 'failed' && 'border-amber-500/70 bg-amber-500/15 text-amber-400',
    )}
    aria-hidden
  >
    {state === 'done' && <Check size={11} strokeWidth={3} />}
    {state === 'failed' && <AlertTriangle size={10} strokeWidth={2.5} />}
    {state === 'active' && <span className="h-2 w-2 rounded-full bg-accent ship-pulse" />}
  </span>
)

/** Where the newest release is, stage by stage, with the time since it was tagged. */
export const ShipPipeline = ({ builds, unreachable }: { builds: Builds; unreachable: boolean }) => {
  const live = !shipping(builds)
  const now = useNow(live ? 30_000 : 1_000)
  const p = pipelineOf(builds, now, unreachable)
  if (!p) return null
  const failed = p.stages.some((s) => s.state === 'failed')
  return (
    <div className="card p-3" data-testid="ship-pipeline" data-live={p.live}>
      <div className="flex items-baseline gap-2 mb-2.5">
        <span className="font-semibold">
          {p.live ? 'Live on prod' : failed ? 'Stuck on the way to prod' : 'Shipping to prod'}
        </span>
        <span className="font-mono text-[11px] text-faint">{p.sha}</span>
        <span className="ml-auto text-[12px] text-muted tabular-nums">
          {p.live ? `shipped ${elapsed(now - Date.parse(p.at))} ago` : elapsed(now - Date.parse(p.at))}
        </span>
      </div>
      <ol className="relative flex flex-col gap-2.5 before:absolute before:left-[9.5px] before:top-2 before:bottom-2 before:w-px before:bg-line">
        {p.stages.map((s) => (
          <li
            key={s.key}
            className="flex items-start gap-2.5 min-w-0"
            data-stage={s.key}
            data-state={s.state}
          >
            <Dot state={s.state} />
            <span className="min-w-0 pt-px">
              <span
                className={cx(
                  'block text-[13.5px] leading-5',
                  s.state === 'waiting'
                    ? 'text-faint'
                    : s.state === 'active'
                      ? 'text-fg font-medium'
                      : 'text-fg-2',
                )}
              >
                {s.label}
              </span>
              {s.detail && (
                <span
                  className={cx(
                    'block text-[12px] leading-snug break-words',
                    s.key === 'tagged' ? 'font-mono text-[11px] text-faint' : 'text-muted',
                  )}
                >
                  {s.detail}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}
